import { Link } from "react-router-dom";
import { 
  Shield, 
  ArrowLeft, 
  Lock, 
  Key, 
  Server, 
  Eye, 
  FileText, 
  CheckCircle,
  ShieldCheck,
  Database,
  Network,
  AlertTriangle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

export default function SecurityPage() {
  const securityFeatures = [
    {
      icon: Key,
      title: "Device Token Authentication",
      description: "No API keys stored in agents. Secure device tokens are generated server-side and rotated automatically."
    },
    {
      icon: Lock,
      title: "HMAC-SHA256 Signatures",
      description: "All payloads are cryptographically signed to ensure integrity and prevent tampering during transmission."
    },
    {
      icon: AlertTriangle,
      title: "Replay Attack Protection",
      description: "Timestamps and nonces prevent replay attacks. Requests older than 5 minutes are automatically rejected."
    },
    {
      icon: Database,
      title: "Encrypted Storage",
      description: "Sensitive data is encrypted at rest using industry-standard AES-256 encryption."
    },
    {
      icon: Network,
      title: "TLS 1.3 Transport",
      description: "All data in transit is protected with TLS 1.3 encryption for maximum security."
    },
    {
      icon: Eye,
      title: "Privacy Mode",
      description: "Optional hashing of user identifiers (SIDs) for GDPR-compliant data collection."
    }
  ];

  const complianceItems = [
    {
      title: "Data Isolation",
      description: "Multi-tenant architecture ensures complete data isolation between organizations."
    },
    {
      title: "Row-Level Security",
      description: "Database-level RLS policies prevent unauthorized data access at the infrastructure level."
    },
    {
      title: "Audit Logging",
      description: "Comprehensive audit trails for all administrative actions and data access."
    },
    {
      title: "Rate Limiting",
      description: "Per-device rate limiting prevents abuse and protects service availability."
    }
  ];

  return (
    <div className="min-h-screen bg-[hsl(224,71%,4%)] text-white">
      {/* Header */}
      <header className="border-b border-white/10 sticky top-0 z-50 bg-[hsl(224,71%,4%)]/95 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4">
          <nav className="flex items-center justify-between">
            <Link to="/admin-access" className="flex items-center gap-3">
              <div className="p-2 bg-primary rounded-lg">
                <Shield className="w-6 h-6 text-primary-foreground" />
              </div>
              <span className="text-xl font-bold">ServSpect</span>
            </Link>
            <Button variant="ghost" asChild className="text-white hover:bg-white/10">
              <Link to="/admin-access">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Link>
            </Button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-b from-primary/10 to-transparent">
        <div className="container mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 border border-primary/30 mb-6">
            <ShieldCheck className="w-5 h-5 text-primary" />
            <span className="text-primary font-medium">Enterprise-Grade Security</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Security at ServSpect
          </h1>
          <p className="text-xl text-white/70 max-w-3xl mx-auto">
            We built ServSpect with security-first principles. Your server data is protected 
            with industry-leading encryption, authentication, and access controls.
          </p>
        </div>
      </section>

      {/* Security Features */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-4 text-center">Security Features</h2>
          <p className="text-white/60 text-center mb-12 max-w-2xl mx-auto">
            Every layer of ServSpect is designed with security in mind, from agent authentication 
            to data storage and transmission.
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {securityFeatures.map((feature, index) => (
              <Card key={index} className="bg-white/5 border-white/10">
                <CardHeader>
                  <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <CardTitle className="text-white">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-white/60">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Agent Security */}
      <section className="py-16 bg-[hsl(224,71%,6%)]">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">Agent Security Architecture</h2>
              <p className="text-white/70 mb-6">
                The ServSpect agent is designed with zero-trust principles. It never stores 
                sensitive API keys and uses a secure device token system for authentication.
              </p>
              
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary mt-0.5" />
                  <div>
                    <strong className="text-white">NTFS ACL Protection</strong>
                    <p className="text-white/60 text-sm">
                      Configuration files are protected with SYSTEM and Administrators-only permissions
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary mt-0.5" />
                  <div>
                    <strong className="text-white">Enrollment Token Cleanup</strong>
                    <p className="text-white/60 text-sm">
                      One-time enrollment tokens are immediately removed after successful registration
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary mt-0.5" />
                  <div>
                    <strong className="text-white">Message Scrubbing</strong>
                    <p className="text-white/60 text-sm">
                      Passwords, API keys, and sensitive data are automatically redacted from event logs
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary mt-0.5" />
                  <div>
                    <strong className="text-white">Token Expiry Validation</strong>
                    <p className="text-white/60 text-sm">
                      Agents validate token expiry hourly and alert when re-registration is needed
                    </p>
                  </div>
                </li>
              </ul>
            </div>
            
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <Server className="w-6 h-6 text-primary" />
                <span className="font-semibold">Security Flow</span>
              </div>
              <div className="space-y-4 text-sm">
                <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                  <span className="w-6 h-6 bg-primary rounded-full flex items-center justify-center text-xs font-bold">1</span>
                  <span className="text-white/80">Agent generates secure device ID from hardware identifiers</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                  <span className="w-6 h-6 bg-primary rounded-full flex items-center justify-center text-xs font-bold">2</span>
                  <span className="text-white/80">Enrollment token validates and creates device token</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                  <span className="w-6 h-6 bg-primary rounded-full flex items-center justify-center text-xs font-bold">3</span>
                  <span className="text-white/80">Payloads signed with HMAC-SHA256 using device token</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                  <span className="w-6 h-6 bg-primary rounded-full flex items-center justify-center text-xs font-bold">4</span>
                  <span className="text-white/80">Server verifies signature, timestamp, and nonce</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                  <span className="w-6 h-6 bg-primary rounded-full flex items-center justify-center text-xs font-bold">5</span>
                  <span className="text-white/80">Data stored with tenant isolation and RLS</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-4 text-center">Infrastructure Security</h2>
          <p className="text-white/60 text-center mb-12 max-w-2xl mx-auto">
            Our infrastructure is designed to meet the security requirements of enterprise customers.
          </p>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {complianceItems.map((item, index) => (
              <div key={index} className="flex items-start gap-4 p-6 bg-white/5 border border-white/10 rounded-xl">
                <CheckCircle className="w-6 h-6 text-primary flex-shrink-0" />
                <div>
                  <h3 className="font-semibold mb-2">{item.title}</h3>
                  <p className="text-white/60 text-sm">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Report Vulnerability */}
      <section className="py-16 bg-[hsl(224,71%,6%)]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Report a Vulnerability</h2>
          <p className="text-white/60 mb-8 max-w-2xl mx-auto">
            We take security seriously. If you discover a security vulnerability, please report it to us responsibly.
          </p>
          <Button asChild className="bg-primary hover:bg-primary/90">
            <a href="mailto:security@servspect.com">
              Contact Security Team
            </a>
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 bg-[hsl(224,71%,4%)]">
        <div className="container mx-auto px-4 text-center text-white/60 text-sm">
          <p>© {new Date().getFullYear()} ServSpect. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
